import { BaseService } from '../api/base.service';

export interface NotificationPayload {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  data?: Record<string, any>;
  actions?: NotificationAction[];
  requireInteraction?: boolean;
  silent?: boolean;
}

export interface NotificationAction {
  action: string;
  title: string;
  icon?: string;
}

export interface NotificationPermissionResult {
  granted: boolean;
  permission: NotificationPermission;
  reason?: string;
}

export class PushNotificationService extends BaseService {
  private static instance: PushNotificationService;
  
  static getInstance(): PushNotificationService {
    if (!PushNotificationService.instance) {
      PushNotificationService.instance = new PushNotificationService();
    }
    return PushNotificationService.instance;
  }

  /**
   * Check if browser supports notifications
   */
  isSupported(): boolean {
    return 'Notification' in window;
  }

  /**
   * Get current notification permission status
   */
  getPermissionStatus(): NotificationPermission {
    if (!this.isSupported()) {
      return 'denied';
    }
    return Notification.permission;
  }

  /**
   * Request notification permission from user
   */
  async requestPermission(): Promise<NotificationPermissionResult> {
    if (!this.isSupported()) {
      return {
        granted: false,
        permission: 'denied',
        reason: 'Browser does not support notifications'
      };
    }

    if (this.getPermissionStatus() === 'granted') {
      return {
        granted: true,
        permission: 'granted'
      };
    }

    try {
      const permission = await Notification.requestPermission();
      
      return {
        granted: permission === 'granted',
        permission,
        reason: permission === 'denied' ? 'User denied notification permission' : undefined
      };
    } catch (error) {
      console.error('Failed to request notification permission:', error);
      return {
        granted: false,
        permission: 'denied',
        reason: 'Failed to request permission'
      };
    }
  }

  /**
   * Show a notification
   */
  async showNotification(payload: NotificationPayload): Promise<boolean> {
    const permissionResult = await this.ensurePermission();
    
    if (!permissionResult.granted) {
      console.warn('Cannot show notification: permission not granted');
      return false;
    }

    try {
      const notification = new Notification(payload.title, {
        body: payload.body,
        icon: payload.icon || '/favicon.ico',
        badge: payload.badge || '/favicon.ico',
        tag: payload.tag,
        data: payload.data,
        requireInteraction: payload.requireInteraction || false,
        silent: payload.silent || false,
        // Note: actions require service worker registration
      });

      // Auto-close after 8 seconds unless requireInteraction is true
      if (!payload.requireInteraction) {
        setTimeout(() => {
          notification.close();
        }, 8000);
      }

      // Handle notification click
      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        
        // Handle custom data actions
        if (payload.data?.url) {
          window.location.href = payload.data.url;
        } else if (payload.data?.action) {
          this.handleNotificationAction(payload.data.action, payload.data);
        }
        
        notification.close();
      };

      notification.onerror = (error) => {
        console.error('Notification error:', error);
      };

      return true;
    } catch (error) {
      console.error('Failed to show notification:', error);
      return false;
    }
  }

  /**
   * Show @mention notification
   */
  async showMentionNotification(data: {
    mentionedBy: string;
    clubName: string;
    channelName: string;
    messagePreview: string;
    clubId: string;
    channelId: string;
  }): Promise<boolean> {
    return this.showNotification({
      title: `💬 ${data.mentionedBy} mentioned you`,
      body: `In ${data.clubName} #${data.channelName}: "${data.messagePreview}"`,
      icon: '💬',
      tag: `mention_${data.clubId}_${data.channelId}`,
      requireInteraction: true,
      data: {
        type: 'mention',
        action: 'openClubChat',
        clubId: data.clubId,
        channelId: data.channelId,
        url: `/clubs/${data.clubId}/chat?channel=${data.channelId}`
      }
    });
  }

  /**
   * Show round sharing notification
   */
  async showRoundShareNotification(data: {
    playerName: string;
    clubName: string;
    courseName: string;
    score: number;
    parDifferential: number;
    clubId: string;
  }): Promise<boolean> {
    const scoreText = data.parDifferential < 0 
      ? `${Math.abs(data.parDifferential)} under par! 🔥`
      : data.parDifferential === 0 
      ? 'right on par! ⛳'
      : `${data.parDifferential} over par`;

    return this.showNotification({
      title: `${data.playerName} shared a round from ${data.clubName}`,
      body: `Shot a ${data.score} (${scoreText}) at ${data.courseName}`,
      tag: `round_share_${data.clubId}`,
      data: {
        type: 'round_share',
        action: 'openClubChat',
        clubId: data.clubId,
        url: `/clubs/${data.clubId}/chat?channel=round-shares`
      }
    });
  }

  /**
   * Show badge achievement notification
   */
  async showBadgeNotification(data: {
    badgeName: string;
    badgeIcon: string;
    points: number;
    description: string;
  }): Promise<boolean> {
    return this.showNotification({
      title: `🏆 Badge Unlocked: ${data.badgeName}!`,
      body: `${data.description} (+${data.points} points)`,
      icon: data.badgeIcon,
      tag: `badge_${data.badgeName}`,
      requireInteraction: true,
      data: {
        type: 'badge',
        action: 'openProfile',
        url: '/profile?tab=badges'
      }
    });
  }

  /**
   * Ensure notification permission is granted
   */
  private async ensurePermission(): Promise<NotificationPermissionResult> {
    const currentStatus = this.getPermissionStatus();
    
    if (currentStatus === 'granted') {
      return { granted: true, permission: 'granted' };
    }
    
    if (currentStatus === 'denied') {
      return {
        granted: false,
        permission: 'denied',
        reason: 'Notification permission was previously denied'
      };
    }
    
    // Request permission if not yet determined
    return this.requestPermission();
  }

  /**
   * Handle notification action clicks
   */
  private handleNotificationAction(action: string, data: any): void {
    switch (action) {
      case 'openClubChat':
        if (data.clubId) {
          // Navigate to club chat
          console.log('Opening club chat:', data.clubId);
        }
        break;
        
      case 'openProfile':
        // Navigate to profile
        console.log('Opening profile');
        break;
        
      default:
        console.log('Unknown notification action:', action);
    }
  }

  /**
   * Clear all notifications with a specific tag
   */
  clearNotifications(tag: string): void {
    // Note: This requires service worker registration to work properly
    // For now, we'll rely on auto-close timeouts
    console.log('Clearing notifications with tag:', tag);
  }

  /**
   * Get notification preferences from user settings
   */
  async getNotificationPreferences(): Promise<{
    mentions: boolean;
    roundShares: boolean;
    badges: boolean;
    doNotDisturb: boolean;
  }> {
    // This would typically come from user preferences in the database
    // For now, return defaults
    return {
      mentions: true,
      roundShares: true,
      badges: true,
      doNotDisturb: false
    };
  }

  /**
   * Check if notifications should be shown based on user preferences
   */
  async shouldShowNotification(type: 'mentions' | 'roundShares' | 'badges'): Promise<boolean> {
    const preferences = await this.getNotificationPreferences();
    
    if (preferences.doNotDisturb) {
      return false;
    }
    
    return preferences[type];
  }
}

export const pushNotificationService = PushNotificationService.getInstance();
export default pushNotificationService;